import { inject, ref } from 'vue'

import { useActiveTenantStore } from '@/application/active-tenant/active-tenant.store'
import { AuthError, isAuthError } from '@/domain/auth'
import { AUTH_API_KEY } from '@/ports'
import type { AuthApi } from '@/ports/auth-api'

import { useSessionStore } from './session.store'

/**
 * useLogout : déclenche POST /auth/logout, clear le store, et laisse au
 * caller le soin de naviguer vers /login (la route guard refera le job
 * de toute façon à la prochaine navigation).
 *
 * Idempotent côté backend : si la session est déjà invalide, /logout
 * retourne 204. Le store est resetté quoi qu'il arrive (best-effort
 * coté serveur, certitude côté client).
 */
export function useLogout() {
    const store = useSessionStore()
    const activeTenantStore = useActiveTenantStore()
    const authApi = injectAuthApi()

    const isLoading = ref(false)
    const error = ref<AuthError | null>(null)

    async function logout(): Promise<void> {
        if (isLoading.value) {
            return
        }
        isLoading.value = true
        error.value = null
        const csrf = store.csrfToken || null
        try {
            await authApi.logout(csrf)
        } catch (cause) {
            // Network error : on ne bloque pas le logout local. La
            // session backend expirera côté serveur (TTL). Le client
            // est définitivement out, c'est ce qui compte côté UX.
            error.value = isAuthError(cause)
                ? cause
                : new AuthError('unknown', 'Unexpected logout failure')
        } finally {
            store.setUnauthenticated()
            // Reset le tenant override : un super_admin qui se relogue
            // ne doit pas voir un tenant filtré silencieusement.
            activeTenantStore.reset()
            isLoading.value = false
        }
    }

    return { isLoading, error, logout }
}

function injectAuthApi(): AuthApi {
    const api = inject(AUTH_API_KEY, null)
    if (!api) {
        throw new AuthError('internal_error', 'AuthApi not provided (bootstrap bug)')
    }
    return api
}
